import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import type { Tema } from '../types'

export const TEMAS = [
  { valor: 'Naranja', nombre: 'Naranja', muestra: 'linear-gradient(135deg, #f97316, #fb923c)' },
  { valor: 'blue', nombre: 'Azul', muestra: 'linear-gradient(135deg, #3b82f6, #60a5fa)' },
  { valor: 'green', nombre: 'Verde', muestra: 'linear-gradient(135deg, #22c55e, #4ade80)' },
  { valor: 'red', nombre: 'Rojo', muestra: 'linear-gradient(135deg, #ef4444, #f87171)' },
  { valor: 'yellow', nombre: 'Amarillo', muestra: 'linear-gradient(135deg, #eab308, #facc15)' },
  { valor: 'purple', nombre: 'Morado', muestra: 'linear-gradient(135deg, #a855f7, #c084fc)' },
  { valor: 'black', nombre: 'Negro', muestra: 'linear-gradient(135deg, #1f2937, #4b5563)' },
  { valor: 'gray', nombre: 'Gris', muestra: 'linear-gradient(135deg, #6b7280, #9ca3af)' },
  { valor: 'white', nombre: 'Blanco', muestra: 'linear-gradient(135deg, #ffffff, #e5e7eb)' },
] as const

interface ThemeContextValue {
  tema: Tema
  modoOscuro: boolean
  cambiarTema: (t: Tema, guardar?: boolean) => void
  alternarOscuro: () => void
}

const STORAGE_TEMA = 'logistica-tema'
const STORAGE_MODO_OSCURO = 'logistica-modo-oscuro'

const ThemeContext = createContext<ThemeContextValue | null>(null)

function temaGuardado(): Tema {
  const raw = localStorage.getItem(STORAGE_TEMA)
  if (raw && (TEMAS as readonly { valor: Tema }[]).some((t) => t.valor === raw)) {
    return raw as Tema
  }
  return 'Naranja'
}

function modoOscuroGuardado(): boolean {
  return localStorage.getItem(STORAGE_MODO_OSCURO) === 'true'
}

export function TemaProvider({ children }: { children: ReactNode }) {
  const [tema, setTema] = useState<Tema>(temaGuardado)
  const [modoOscuro, setModoOscuro] = useState(modoOscuroGuardado)

  useEffect(() => {
    document.body.className = `theme-${tema}${modoOscuro ? ' dark' : ''}`
    const color = modoOscuro ? '#1E2836' : '#ffffff'
    const existing = document.querySelector('meta[name="theme-color"]')
    if (existing) existing.remove()
    const meta = document.createElement('meta')
    meta.name = 'theme-color'
    meta.content = color
    document.head.appendChild(meta)
  }, [tema, modoOscuro])

  const cambiarTema = (t: Tema, guardar = true) => {
    setTema(t)
    if (guardar) localStorage.setItem(STORAGE_TEMA, t)
  }
  const alternarOscuro = () => {
    setModoOscuro((v) => {
      const next = !v
      localStorage.setItem(STORAGE_MODO_OSCURO, String(next))
      return next
    })
  }

  return (
    <ThemeContext.Provider value={{ tema, modoOscuro, cambiarTema, alternarOscuro }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTema() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTema debe usarse dentro de TemaProvider')
  return { ...ctx, TEMAS }
}
